import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { api } from "../../api/client";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { useToast } from "../../components/Toast";
import type { TrashItem } from "../../api/types";

// 資源回收桶(業主 2026-09-20)。
//
// > 做一個資源回收桶 7天內可以恢復任何東西
//
// 七種東西混在一張清單上:會議紀錄、代辦、客戶窗口、範本、雜支、出勤打卡、
// 報價單附件。談過之後收斂成這七種 —— 報價單、客戶、品項、發票各自本來就有
// 退路(封存 / 停用 / 作廢),收進來只會多出第三個講「讓它消失但救得回來」的詞。
//
// ==========================================================================
// 每一列要答得出四件事
// ==========================================================================
// **是哪一種、叫什麼、誰丟的、還剩幾天。** 七種混在一起,少了任何一樣都找不到
// 要救的那一筆 ——「雜支」三個字救不了任何人,「舞台桁架運費 3,200」才可以。
//
// ==========================================================================
// 不分頁、不做搜尋
// ==========================================================================
// 七天內的刪除量是個位數到兩位數,一頁看得完。做了分頁只是多一個要維護的東西。
//
// 「還剩幾天」由**後端**算(`app/routers/trash.py`)—— 前端各算一份的話,
// 時區差一小時就會差一天。

/** 表格欄位。最後一欄沒有標題(還原鈕),所以用 `id` 當 key。 */
const HEADERS: { id: string; label: string }[] = [
  { id: "kind", label: "種類" },
  { id: "name", label: "名稱" },
  { id: "who", label: "誰刪的" },
  { id: "when", label: "刪除時間" },
  { id: "left", label: "還剩" },
  { id: "actions", label: "" },
];

/** `2026-09-18T10:00:00+08:00` → `09/18 10:00`。 */
function stamp(iso: string | null): string {
  if (!iso) return "";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(when.getMonth() + 1)}/${two(when.getDate())} ` +
    `${two(when.getHours())}:${two(when.getMinutes())}`;
}

export function TrashPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ["trash"],
    queryFn: () => api.get<{ items: TrashItem[] }>("/trash"),
  });
  const items = data?.items ?? [];

  const restore = useMutation({
    mutationFn: (one: TrashItem) =>
      api.post(`/trash/${one.kind}/${one.id}/restore`),
    onSuccess: (_got, one) => {
      toast.push(`「${one.name}」已經放回去了`, "success");
      void queryClient.invalidateQueries({ queryKey: ["trash"] });
      // 救回來的東西要在它原本那一頁上立刻看得到。七種各有各的查詢鍵,
      // 與其逐一列舉(漏一個就是「救回來了但那一頁還是空的」),整份重查。
      void queryClient.invalidateQueries();
    },
    onError: (err: unknown) => {
      toast.push(err instanceof Error ? err.message : "放不回去", "error");
    },
  });

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <h2 className="m-0 text-xl font-semibold text-[var(--text)]">回收桶</h2>
      <p className="m-0 text-sm text-[var(--text-2)]">
        刪掉的東西會放在這裡 <strong>七天</strong>,期間隨時按「還原」就放得回去;
        七天之後系統會真的清掉,那時候就救不回來了。
        你看得到的是自己刪的那幾筆(業主看得到全部)。
      </p>

      {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          回收桶載入失敗,請稍後再試。
        </p>
      ) : null}

      {items.length > 0 ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="回收桶" className="w-full min-w-[40rem] border-collapse text-left">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                {HEADERS.map((header) => (
                  <th key={header.id} scope="col" className="whitespace-nowrap px-3 py-2">
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((one) => (
                <tr key={`${one.kind}-${one.id}`} className="border-t border-[var(--border)]">
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-[var(--text-2)]">
                    {one.kind_label}
                  </td>
                  <td className="px-3 py-2 text-sm text-[var(--text)]">{one.name}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-[var(--text-2)]">
                    {one.deleted_by_name}
                  </td>
                  <td className="tabular whitespace-nowrap px-3 py-2 text-sm text-[var(--text-2)]">
                    {stamp(one.deleted_at)}
                  </td>
                  {/* 剩一天的那幾筆要看得出來 —— 那是最後一次機會。 */}
                  <td
                    className={
                      "tabular whitespace-nowrap px-3 py-2 text-sm " +
                      (one.days_left <= 1 ? "text-[var(--danger)]" : "text-[var(--text-2)]")
                    }
                  >
                    {`${one.days_left} 天`}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button
                      type="button"
                      onClick={() => restore.mutate(one)}
                      icon={<RotateCcw size={15} aria-hidden="true" />}
                    >
                      還原
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {!isLoading && !error && items.length === 0 ? (
        <EmptyState
          title="回收桶裡沒有東西。"
          description={
            "刪掉的會議紀錄、代辦、客戶窗口、範本、雜支、出勤打卡、報價單附件與工單" +
            "會放在這裡七天。報價單與客戶不在這裡 —— 它們各自有封存與停用。"
          }
        />
      ) : null}
    </div>
  );
}
